import type { WordTiming, ItemMetrics, SessionItem, SessionSummary } from '../types';

// Starter filler list (handoff §5). Multi-word fillers are matched first and
// counted as one. Order matters only for readability — matching is independent.
export const FILLERS = [
  'you know',
  'so yeah',
  'kind of',
  'sort of',
  'i mean',
  'um',
  'uh',
  'er',
  'erm',
  'like',
  'basically',
  'actually',
  'literally',
] as const;

const LONG_PAUSE_S = 2.0;

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Count fillers in a transcript, word-boundary aware and case-insensitive.
 * Multi-word fillers ("so yeah") count as one. Returns total + per-filler map.
 */
export function countFillers(transcript: string): { filler_count: number; fillers: Record<string, number> } {
  const fillers: Record<string, number> = {};
  let total = 0;
  for (const f of FILLERS) {
    // \b doesn't sit well before/after some tokens, so bound on non-letters.
    const re = new RegExp(`(?:^|[^a-z])(${escapeRe(f)})(?![a-z])`, 'gi');
    let n = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(transcript.toLowerCase())) !== null) {
      n++;
      // step back one char so adjacent matches ("um um") are both caught
      if (m.index + m[0].length > re.lastIndex) re.lastIndex = m.index + m[0].length;
    }
    if (n > 0) {
      fillers[f] = n;
      total += n;
    }
  }
  return { filler_count: total, fillers };
}

/**
 * Compute per-answer metrics off the transcript + Whisper word timings.
 * Word timings are relative to the start of the answer recording, which begins
 * the instant the question audio ends — so words[0].start IS the response latency.
 */
export function computeMetrics(
  words: WordTiming[] | undefined,
  transcript: string | null,
  fallbackDurationS: number,
): ItemMetrics | null {
  if (!transcript || !words || words.length === 0) return null;

  const first = words[0];
  const last = words[words.length - 1];
  const talk_time_s = Math.max(0, last.end - first.start);
  const wordCount = words.length;
  const wpm = talk_time_s > 0 ? Math.round((wordCount / talk_time_s) * 60) : 0;
  const response_latency_s = Math.max(0, first.start);

  let long_pauses = 0;
  for (let i = 1; i < words.length; i++) {
    if (words[i].start - words[i - 1].end > LONG_PAUSE_S) long_pauses++;
  }

  const { filler_count, fillers } = countFillers(transcript);

  return {
    talk_time_s: round1(talk_time_s || fallbackDurationS),
    words: wordCount,
    wpm,
    response_latency_s: round1(response_latency_s),
    filler_count,
    fillers,
    long_pauses,
  };
}

export function summarize(items: SessionItem[]): SessionSummary {
  const answered = items.filter((it) => it.metrics);
  const n = answered.length;
  const total_talk_time_s = answered.reduce((s, it) => s + (it.metrics!.talk_time_s || 0), 0);
  const total_words = answered.reduce((s, it) => s + (it.metrics!.words || 0), 0);
  const total_fillers = answered.reduce((s, it) => s + (it.metrics!.filler_count || 0), 0);
  const total_latency = answered.reduce((s, it) => s + (it.metrics!.response_latency_s || 0), 0);
  // Weighted wpm over the whole session, not a mean-of-means.
  const avg_wpm = total_talk_time_s > 0 ? Math.round((total_words / total_talk_time_s) * 60) : 0;
  return {
    total_talk_time_s: round1(total_talk_time_s),
    avg_wpm,
    total_fillers,
    avg_response_latency_s: n > 0 ? round1(total_latency / n) : 0,
    answered: n,
  };
}

export function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

export function fmtClock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Render a transcript with fillers wrapped for the review view (returns segments).
export interface TSeg { text: string; filler: boolean; }
export function segmentTranscript(transcript: string): TSeg[] {
  if (!transcript) return [];
  const alt = FILLERS.map(escapeRe).join('|');
  const re = new RegExp(`(^|[^a-zA-Z])(${alt})(?![a-zA-Z])`, 'gi');
  const segs: TSeg[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(transcript)) !== null) {
    const lead = m[1] ?? '';
    const fillerStart = m.index + lead.length;
    if (fillerStart > last) segs.push({ text: transcript.slice(last, fillerStart), filler: false });
    segs.push({ text: transcript.slice(fillerStart, fillerStart + m[2].length), filler: true });
    last = fillerStart + m[2].length;
  }
  if (last < transcript.length) segs.push({ text: transcript.slice(last), filler: false });
  return segs;
}
